import Spinner from '@app/lui2/Spinner';
import {useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {
  addDebouncedFocusVisibleRefetch,
  isAbortError,
} from '../../browserRefetch';
import {useOutlookMailboxUiContext} from '../../outlookMailboxEmail';
import {
  fetchOutlookCurrentUser,
  type OutlookCurrentUserJson,
} from '../../requests';
import {usePodRequest} from '../../usePodRequest';
import EmailRecipients from './EmailRecipients';
import EmptyState from './EmptyState';
import NoEmail from './NoEmail';
import SyncEmail from './SyncEmail';

const LoadingSpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 2.75rem 2rem 0 2rem;
`;

const Content = () => {
  const [currentUser, setCurrentUser] = useState<OutlookCurrentUserJson | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const {request} = usePodRequest();
  const {mailboxEmail, isMailItemActive} = useOutlookMailboxUiContext();

  useEffect(() => {
    const controller = new AbortController();

    setIsLoading(true);
    setLoadError(null);

    void (async () => {
      try {
        const data = await fetchOutlookCurrentUser(request, {
          signal: controller.signal,
        });
        if (!controller.signal.aborted) {
          setCurrentUser(data);
        }
      } catch (error) {
        if (!controller.signal.aborted && !isAbortError(error)) {
          setCurrentUser(null);
          setLoadError(
            error instanceof Error
              ? error.message
              : 'Could not load your Loxo account.'
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [request]);

  const mailboxEmailIsSynced = useMemo(() => {
    if (!currentUser || !mailboxEmail) {
      return false;
    }
    return currentUser.current_user.email_accounts.some(
      (account) =>
        account.email_address.toLowerCase() === mailboxEmail.toLowerCase()
    );
  }, [currentUser, mailboxEmail]);

  useEffect(() => {
    if (mailboxEmailIsSynced || !currentUser?.current_user?.first_agency?.id) {
      return;
    }

    let cancelled = false;
    let activeController: AbortController | undefined;
    let latestRequestId = 0;

    const load = () => {
      const requestId = latestRequestId + 1;
      latestRequestId = requestId;
      activeController?.abort();
      const controller = new AbortController();
      activeController = controller;

      fetchOutlookCurrentUser(request, {signal: controller.signal})
        .then((data) => {
          if (
            !cancelled &&
            !controller.signal.aborted &&
            requestId === latestRequestId
          ) {
            setLoadError(null);
            setCurrentUser(data);
          }
        })
        .catch(() => {
          // do nothing
        });
    };

    const removeRefetchListeners = addDebouncedFocusVisibleRefetch(load);

    return () => {
      cancelled = true;
      activeController?.abort();
      removeRefetchListeners();
    };
  }, [
    mailboxEmailIsSynced,
    request,
    currentUser?.current_user?.first_agency?.id,
  ]);

  if (isLoading) {
    return (
      <LoadingSpinnerWrapper
        role="status"
        aria-label="Loading Loxo account details"
      >
        <Spinner />
      </LoadingSpinnerWrapper>
    );
  }

  if (loadError) {
    return (
      <EmptyState
        title="Could not load Loxo"
        subtitle="Something went wrong while loading your Loxo account details."
      />
    );
  }

  if (!currentUser?.current_user?.first_agency?.id) {
    return null;
  }

  const agencyId = currentUser.current_user.first_agency.id;

  if (!mailboxEmailIsSynced) {
    return <SyncEmail agencyId={agencyId} />;
  }

  if (!isMailItemActive) {
    return <NoEmail />;
  }

  return <EmailRecipients agencyId={agencyId} />;
};

export default Content;
